import React, { useState, useEffect } from 'react';
import { Eye, X, Plus, User, AlertTriangle, CheckCircle, Camera, Upload, Check, ArrowLeft, Save, Loader2, CalendarDays } from 'lucide-react';
import { useFetchSpeciality } from '../../hooks/Resources/speciality/useFetchSpeciality';
import useFetchModalities from '../../hooks/Resources/modality/useFetchModalityResource';
import { useFetchAgeRange } from '../../hooks/Resources/age/useFetchAgeRange';
import useFetchLanguage from '../../hooks/Resources/language/useLanguagesResource';
import { useFetchInsuranceResource } from '../../hooks/Resources/insurance/useFetchInsuranceResource';
import useFetchSessionType from '../../hooks/Resources/session/useFetchSessionTypeResource';
import api from '../../redux/api';

// MultiSelect Component
interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ options, selectedValues, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const selectedLabels = options
    .filter(option => selectedValues.includes(option.value))
    .map(option => option.label);

  return (
    <div className="relative">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-2 border rounded-lg mt-1 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1 min-h-6">
            {selectedLabels.length > 0 ? (
              selectedLabels.map((label, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                >
                  {label}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const option = options.find(opt => opt.label === label);
                      if (option) {
                        handleToggle(option.value);
                      }
                    }}
                    className="ml-1 hover:text-blue-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            ) : (
              <span className="text-gray-500">{placeholder || 'Select options'}</span>
            )}
          </div>
          <div className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => handleToggle(option.value)}
              className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
            >
              <div className="flex items-center">
                <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center ${selectedValues.includes(option.value)
                  ? 'bg-blue-600 border-blue-600'
                  : 'border-gray-300'
                  }`}>
                  {selectedValues.includes(option.value) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-sm">{option.label}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Common Titles
const COMMON_TITLES = [
  'PhD', 'PsyD', 'LCSW', 'LMFT', 'LPC', 'MD', 'DO', 'NP', 'PA', 'RN', 'Med', 'MS', 'MA'
];

// Matches the backend schema: clinician.location[{ id_location, name, postal_code }]
interface LocationEntry {
  id_location?: string;
  name?: string;
  postal_code?: string;
}

// Updated interface to match your data structures
interface ClinicianData {
  _id: string;
  clinicianId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  emailSource: string;
  npiNumber: string;
  titles: string[]; // Changed from title
  role: string;
  status: string;
  phone: string;
  specialties: string[];
  licenses: string[]; // Additional Certifications
  licenseDetails: { number: string; type: string; state: string }[]; // State Licenses
  slug: string;
  isActive: boolean;
  id_practice: string;
  createdAt: string;
  updatedAt: string;
  address?: {
    locations?: LocationEntry[];
  };
  profile: {
    detailsScrapedAt: string;
    detailsSource: string;
    editUrl: string;
    bio?: string;
    profileImage?: string;
    vslVideo?: string;
    rates?: Array<{ sessionType: string; amount: number; currency: string }>;
    sessionTypes?: Array<{ type: string; duration: number; rate?: number; sessionTarget?: string }>;
    maxSessions?: { perWeek?: number };
    insuranceAccepted?: string[];
    languages?: string[];
    modalities?: string[];
    ageRanges?: string[];
    sessionLength?: string;
    sessionPrice?: string;
  };
  availability?: {
    weeklySchedule?: {
      monday?: Array<{ start: string; end: string; available: boolean }>;
      tuesday?: Array<{ start: string; end: string; available: boolean }>;
      wednesday?: Array<{ start: string; end: string; available: boolean }>;
      thursday?: Array<{ start: string; end: string; available: boolean }>;
      friday?: Array<{ start: string; end: string; available: boolean }>;
      saturday?: Array<{ start: string; end: string; available: boolean }>;
      sunday?: Array<{ start: string; end: string; available: boolean }>;
    };
    /** Calendar-scraped slots (read-only in edit UI) */
    open?: Array<{ time?: string; eventText?: string; date?: string; unixTime?: number }>;
    booked?: Array<{ time?: string; eventText?: string; date?: string; unixTime?: number }>;
    cancelled?: Array<{ time?: string; eventText?: string; date?: string; unixTime?: number }>;
    outOfOffice?: Array<{ time?: string; eventText?: string; date?: string; unixTime?: number }>;
    blocked?: Array<{ time?: string; eventText?: string; date?: string; unixTime?: number }>;
  };
  availabilityUpdatedAt?: string;
  lastCalendarScrape?: string;
}

interface ClinicianAdminEditProps {
  clinician: ClinicianData;
  onSave: (updatedClinician: ClinicianData) => void;
  onCancel: () => void;
  isSaving?: boolean;
  practiceLocations?: Array<{ id_location: string; name: string; postal_code?: string; address1?: string; city?: string; state?: string; zipCode?: string }>;
}

const ClinicianAdminEdit: React.FC<ClinicianAdminEditProps> = ({ clinician, onSave, onCancel, isSaving = false, practiceLocations = [] }) => {
  const [editedClinician, setEditedClinician] = useState<ClinicianData>({
    ...clinician,
    titles: clinician.titles || [],
    licenseDetails: clinician.licenseDetails || [{ number: '', type: '', state: '' }],
    address: {
      locations: clinician.address?.locations?.length
        ? clinician.address.locations
        : [{ id_location: '', name: '', postal_code: '' }],
    },
    profile: {
      ...clinician.profile,
      bio: clinician.profile.bio || '',
      profileImage: clinician.profile.profileImage || '',
      vslVideo: clinician.profile.vslVideo || '',
      rates: clinician.profile.rates || [],
      sessionTypes: clinician.profile.sessionTypes || [],
      maxSessions: clinician.profile.maxSessions || clinician.maxSessions || { perWeek: undefined },
      insuranceAccepted: clinician.profile.insuranceAccepted || [],
      languages: clinician.profile.languages || ['English'],
      modalities: clinician.profile.modalities || [],
      ageRanges: clinician.profile.ageRanges || []
    },
    availability: clinician.availability || {
      weeklySchedule: {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: []
      }
    }
  });

  // Sync state with props when clinician data changes
  useEffect(() => {
    const locs = clinician.address?.locations?.length
      ? clinician.address.locations
      : [{ id_location: '', name: '', postal_code: '' }];
    setEditedClinician({
      ...clinician,
      titles: clinician.titles || [],
      licenseDetails: clinician.licenseDetails || [{ number: '', type: '', state: '' }],
      address: { locations: locs },
      profile: {
        ...clinician.profile,
        bio: clinician.profile.bio || '',
        profileImage: clinician.profile.profileImage || '',
        vslVideo: clinician.profile.vslVideo || '',
        rates: clinician.profile.rates || [],
        sessionTypes: clinician.profile.sessionTypes || [],
        maxSessions: clinician.profile.maxSessions || clinician.maxSessions || { perWeek: undefined },
        insuranceAccepted: clinician.profile.insuranceAccepted || [],
        languages: clinician.profile.languages || ['English'],
        modalities: clinician.profile.modalities || [],
        ageRanges: clinician.profile.ageRanges || []
      },
      availability: clinician.availability || {
        weeklySchedule: {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        }
      }
    });
  }, [clinician]);

  const [newSessionType, setNewSessionType] = useState({ type: '', duration: '', rate: '', sessionTarget: '' });
  const [showPreview, setShowPreview] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [availableLocations, setAvailableLocations] = useState<LocationEntry[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const { specialties } = useFetchSpeciality();
  const { modalities } = useFetchModalities();
  const { ages, fetchAgeRanges } = useFetchAgeRange();
  const { languages, fetchLanguages } = useFetchLanguage();
  const { insurances, fetchInsuranceData } = useFetchInsuranceResource();
  const { sessionTypes, fetchSessionTypeData } = useFetchSessionType()


  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('profile.')) {
      const profileField = field.replace('profile.', '');
      setEditedClinician(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          [profileField]: value
        }
      }));
    } else {
      setEditedClinician(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };



  const addToArray = (field: string, newItem: any) => {
    const currentArray = field.startsWith('profile.')
      ? editedClinician.profile[field.replace('profile.', '') as keyof typeof editedClinician.profile] as any[]
      : editedClinician[field as keyof ClinicianData] as any[];

    handleInputChange(field, [...(currentArray || []), newItem]);
  };

  const removeFromArray = (field: string, index: number) => {
    const currentArray = field.startsWith('profile.')
      ? editedClinician.profile[field.replace('profile.', '') as keyof typeof editedClinician.profile] as any[]
      : editedClinician[field as keyof ClinicianData] as any[];

    handleInputChange(field, currentArray.filter((_, i) => i !== index));
  };

  const handleSave = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    
    let finalClinician = { ...editedClinician };
    
    // Auto-add any pending session type that the user might have filled but forgot to click Add
    if (newSessionType.type && newSessionType.duration) {
      finalClinician = {
        ...finalClinician,
        profile: {
          ...finalClinician.profile,
          sessionTypes: [
            ...(finalClinician.profile.sessionTypes || []),
            {
              type: newSessionType.type,
              duration: parseInt(newSessionType.duration),
              rate: newSessionType.rate ? parseFloat(newSessionType.rate) : undefined,
              sessionTarget: newSessionType.sessionTarget || undefined
            }
          ]
        }
      };
      setNewSessionType({ type: '', duration: '', rate: '', sessionTarget: '' });
    }
    
    onSave(finalClinician);
  };



  // Check if all required fields for public search visibility are filled
  const getPublicVisibilityStatus = () => {
    const requiredFields = [
      { name: 'Specialties', value: (editedClinician.specialties?.length || 0) > 0 },
      { name: 'Rates', value: (editedClinician.profile.rates?.length || 0) > 0 },
      { name: 'Modalities', value: (editedClinician.profile.modalities?.length || 0) > 0 },
      { name: 'Age Ranges', value: (editedClinician.profile.ageRanges?.length || 0) > 0 },
      { name: 'Session Types', value: (editedClinician.profile.sessionTypes?.length || 0) > 0 },
      { name: 'Insurance Accepted', value: (editedClinician.profile.insuranceAccepted?.length || 0) > 0 },
      { name: 'Languages', value: (editedClinician.profile.languages?.length || 0) > 0 }
    ];

    const missingFields = requiredFields.filter(field => !field.value);
    const isComplete = missingFields.length === 0;

    return { isComplete, missingFields, completedCount: requiredFields.length - missingFields.length, totalCount: requiredFields.length };
  };

  const visibilityStatus = getPublicVisibilityStatus();

  useEffect(() => {
    fetchAgeRanges();
  }, [fetchAgeRanges]);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  useEffect(() => {
    fetchSessionTypeData();
  }, [fetchSessionTypeData]);

  useEffect(() => {
    fetchInsuranceData();
  }, [fetchInsuranceData]);

  useEffect(() => {
    const fetchLocations = async () => {
      setLocationsLoading(true);
      try {
        // Use practice locations if provided via props
        if (practiceLocations && practiceLocations.length > 0) {
          setAvailableLocations(practiceLocations.map(loc => ({
            id_location: loc.id_location || loc.id || loc.name || '',
            name: loc.name || loc.address1 || '',
            postal_code: loc.postal_code || loc.zipCode || '',
          })));
          setLocationsLoading(false);
          return;
        }
        // Otherwise fetch from API
        const response = await api.get('/api/locations/available');
        setAvailableLocations(response.data);
      } catch {
        // Fall back to sample data while endpoint is unavailable
        setAvailableLocations([
          { id_location: 'loc-001', name: 'Downtown Miami', postal_code: '33101' },
          { id_location: 'loc-002', name: 'Coral Gables', postal_code: '33134' },
          { id_location: 'loc-003', name: 'Brickell', postal_code: '33131' },
          { id_location: 'loc-004', name: 'Wynwood', postal_code: '33127' },
          { id_location: 'loc-005', name: 'Coconut Grove', postal_code: '33133' },
        ]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, [practiceLocations]);

  // Helper: upload image/video to S3 via presigned URL endpoint
  const uploadMedia = async (file: File, type: 'image' | 'video'): Promise<string> => {
    setUploadError(null);
    try {
      // Request presigned URL from backend
      const extensionMatch = file.name.split('.').pop();
      const fileExtension = extensionMatch ? extensionMatch.toLowerCase() : '';

      const presignResponse = await api.post('/upload/presigned-url', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        fileExtension,
        type,
        clinicianId: editedClinician.clinicianId || editedClinician._id || editedClinician.email || undefined,
      });

      // Lambda may return body nested or at top-level
      const body = presignResponse.data?.body || presignResponse.data;
      const { uploadUrl, publicUrl } = body || {};

      if (!uploadUrl) {
        throw new Error('Missing uploadUrl from presigned URL response');
      }

      // Upload file directly to S3 — do NOT send Authorization or other auth headers;
      // presigned URLs are signed in the query string and S3 rejects extra auth headers.
      const putHeaders: Record<string, string> = {
        'Content-Type': file.type || 'application/octet-stream',
      };
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: putHeaders,
        credentials: 'omit',
      });
      if (!uploadResponse.ok) {
        const errText = await uploadResponse.text();
        throw new Error(errText || `S3 upload failed: ${uploadResponse.status}`);
      }

      if (!publicUrl) {
        // Fallback: if backend didn't return publicUrl, try constructing from other fields
        if (body.bucket && body.region && body.fileKey) {
          return `https://${body.bucket}.s3.${body.region}.amazonaws.com/${body.fileKey}`;
        }
        throw new Error('Upload succeeded but no public URL was returned');
      }

      return publicUrl as string;
    } catch (error: any) {
      console.error('Error uploading media:', error);
      setUploadError(error?.message || 'Failed to upload file. Please try again.');
      throw error;
    }
  };

  // Normalize sessionTypes: map names to IDs when sessionTypes resource is loaded
  useEffect(() => {
    if (sessionTypes && sessionTypes.length > 0 && editedClinician.profile.sessionTypes && editedClinician.profile.sessionTypes.length > 0) {
      let needsUpdate = false;
      const normalized = editedClinician.profile.sessionTypes.map((st: any) => {
        // If type is already an ID (matches one of the sessionTypes resource IDs), keep it
        const matchingById = sessionTypes.find((stResource: any) => stResource.id === st.type || stResource._id === st.type);
        if (matchingById) {
          return st; // Already has correct ID
        }
        
        // If type is a name, try to find matching ID
        const matchingByName = sessionTypes.find((stResource: any) => 
          stResource.name === st.type || 
          stResource.name === st.sessionType ||
          stResource.type === st.type
        );
        
        if (matchingByName) {
          needsUpdate = true;
          return {
            ...st,
            type: matchingByName.id || matchingByName._id || st.type
          };
        }
        
        // If no match found, keep as-is (might be a new custom type)
        return st;
      });
      
      // Only update if normalization changed anything
      if (needsUpdate) {
        handleInputChange('profile.sessionTypes', normalized);
      }
    }
  }, [sessionTypes]); // Only depend on sessionTypes resource, not on editedClinician to avoid loops

  const navigationItems = [
    { id: 'basic', label: 'Basic Info', icon: '👤' },
    { id: 'location', label: 'Location', icon: '📍' },
    { id: 'media', label: 'Media', icon: '📸' },
    { id: 'professional', label: 'Professional', icon: '🎓' },
    { id: 'services', label: 'Services', icon: '💼' }
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'basic':
        return (
          <div className="">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold mb-6">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={`${editedClinician.firstName} ${editedClinician.lastName}`}
                    onChange={(e) => {
                      const names = e.target.value.split(' ');
                      handleInputChange('firstName', names[0] || '');
                      handleInputChange('lastName', names.slice(1).join(' ') || '');
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Credentials (Titles)</label>
                  <MultiSelect
                    options={COMMON_TITLES.map(t => ({ value: t, label: t }))}
                    selectedValues={editedClinician.titles || []}
                    onChange={(selected) => handleInputChange('titles', selected)}
                    placeholder="Select credentials"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Professional Title *</label>
                  <input
                    type="text"
                    placeholder="e.g., Licensed Marriage and Family Therapist"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={editedClinician.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                  />
                </div>

                {/* State Licenses Section */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">State Licenses</label>
                  {editedClinician.licenseDetails?.map((license, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="License Number"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                        value={license.number}
                        onChange={(e) => {
                          const updated = [...editedClinician.licenseDetails];
                          updated[index] = { ...updated[index], number: e.target.value };
                          handleInputChange('licenseDetails', updated);
                        }}
                      />
                      <input
                        type="text"
                        placeholder="License Type"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                        value={license.type}
                        onChange={(e) => {
                          const updated = [...editedClinician.licenseDetails];
                          updated[index] = { ...updated[index], type: e.target.value };
                          handleInputChange('licenseDetails', updated);
                        }}
                      />
                      <input
                        type="text"
                        placeholder="State (e.g. NY)"
                        className="w-24 px-4 py-2 border border-gray-300 rounded-lg"
                        value={license.state}
                        onChange={(e) => {
                          const updated = [...editedClinician.licenseDetails];
                          updated[index] = { ...updated[index], state: e.target.value };
                          handleInputChange('licenseDetails', updated);
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updated = editedClinician.licenseDetails.filter((_, i) => i !== index);
                          handleInputChange('licenseDetails', updated);
                        }}
                        className="p-2 text-red-500 hover:text-red-700"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => handleInputChange('licenseDetails', [...(editedClinician.licenseDetails || []), { number: '', type: '', state: '' }])}
                    className="text-blue-600 text-sm hover:underline flex items-center"
                  >
                    <Plus size={16} className="mr-1" /> Add License
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">NPI Number</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={editedClinician.npiNumber}
                    onChange={(e) => handleInputChange('npiNumber', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={editedClinician.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Biography *</label>
                <div className="relative">
                  <textarea
                    rows={6}
                    maxLength={750}
                    placeholder="Write a compelling bio that introduces yourself to potential clients..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={editedClinician.profile.bio || ''}
                    onChange={(e) => handleInputChange('profile.bio', e.target.value)}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-500">Aim for 3 well-written paragraphs introducing your experience and approach</p>
                    <div className="text-xs text-gray-500">{(editedClinician.profile.bio || '').length}/750 characters</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold mb-4">Specialties</h3>
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {editedClinician.specialties.map((specialty, index) => (
                    <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {specialty}
                      <button
                        className="hover:bg-blue-200 rounded-full p-0.5"
                        onClick={() => removeFromArray('specialties', index)}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Add Specialty</label>
                <MultiSelect
                  options={specialties.map(s => ({ value: s.name, label: s.name }))}
                  selectedValues={editedClinician.specialties}
                  onChange={(selected) => handleInputChange('specialties', selected)}
                  placeholder="Select a specialty..."
                />
              </div>
            </div>
          </div>
        );

      case 'location':
        return (
          <div className="">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold mb-2">Locations</h2>
              <p className="text-gray-600 mb-6">Select practice or office locations. These appear on the clinician profile.</p>
              {locationsLoading ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Loader2 size={16} className="animate-spin" /> Loading locations...
                </div>
              ) : (
                <MultiSelect
                  options={availableLocations.map(loc => ({
                    value: loc.id_location || loc.name || '',
                    label: `${loc.name}${loc.postal_code ? ' \u2014 ' + loc.postal_code : ''}`,
                  }))}
                  selectedValues={
                    (editedClinician.address?.locations || [])
                      .map(l => l.id_location || l.name || '')
                      .filter(Boolean)
                  }
                  onChange={(selected) => {
                    const next = selected
                      .map(val => availableLocations.find(l => (l.id_location || l.name) === val))
                      .filter((l): l is LocationEntry => !!l);
                    setEditedClinician(prev => ({
                      ...prev,
                      address: { locations: next },
                    }));
                  }}
                  placeholder="Select locations..."
                />
              )}
            </div>
          </div>
        );

      case 'media':
        return (
          <div className="">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold mb-4">Profile Media</h2>
              <p className="text-gray-600 mb-6">
                Upload and manage this clinician&apos;s profile photo and intro video. These assets are stored securely in S3.
              </p>

              {uploadError && (
                <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
                  {uploadError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Profile Photo */}
                <div className="bg-gray-50 rounded-xl p-4 border border-dashed border-gray-200">
                  <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4 text-gray-600" />
                    Profile Photo
                  </h3>
                  <div className="flex items-center gap-6">
                    <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                      {editedClinician.profile.profileImage ? (
                        <img
                          src={editedClinician.profile.profileImage}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Camera className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        id="profile-upload"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            setIsUploadingImage(true);
                            const url = await uploadMedia(file, 'image');
                            handleInputChange('profile.profileImage', url);
                          } finally {
                            setIsUploadingImage(false);
                          }
                        }}
                      />
                      <label
                        htmlFor="profile-upload"
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer disabled:opacity-50"
                      >
                        {isUploadingImage ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload Photo
                          </>
                        )}
                      </label>
                      <p className="text-sm text-gray-500 mt-2">Recommended: 400x400px, JPG or PNG</p>
                    </div>
                  </div>
                </div>

                {/* Profile Video */}
                <div className="bg-gray-50 rounded-xl p-4 border border-dashed border-gray-200">
                  <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
                    <Upload className="w-4 h-4 text-gray-600" />
                    Intro Video
                  </h3>
                  <div className="flex flex-col gap-4">
                    {editedClinician.profile.vslVideo ? (
                      <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
                        <video
                          src={editedClinician.profile.vslVideo}
                          className="w-full h-full object-contain"
                          controls
                        />
                        <button
                          onClick={() => handleInputChange('profile.vslVideo', '')}
                          className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full shadow hover:bg-red-700"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-gray-300">
                        <p className="text-gray-500 mb-2">No video uploaded</p>
                        <p className="text-xs text-gray-400 px-4 text-center">
                          Record or upload a short intro video to help clients get to know this clinician.
                        </p>
                      </div>
                    )}

                    <div>
                      <input
                        type="file"
                        accept="video/*"
                        id="video-upload"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 50 * 1024 * 1024) {
                            alert('File size exceeds 50MB');
                            return;
                          }
                          try {
                            setIsUploadingVideo(true);
                            const url = await uploadMedia(file, 'video');
                            handleInputChange('profile.vslVideo', url);
                          } finally {
                            setIsUploadingVideo(false);
                          }
                        }}
                      />
                      <label
                        htmlFor="video-upload"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer bg-white disabled:opacity-50"
                      >
                        {isUploadingVideo ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload Video
                          </>
                        )}
                      </label>
                      <p className="text-sm text-gray-500 mt-2">Recommended: 30-60 seconds, MP4 format, max 50MB</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'professional':
        return (
          <div className="">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold mb-6">Professional Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Modalities</label>
                  <MultiSelect
                    options={modalities.map(m => ({ value: m.name, label: m.name }))}
                    selectedValues={editedClinician.profile.modalities || []}
                    onChange={(selected) => handleInputChange('profile.modalities', selected)}
                    placeholder="Select modalities"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Age Ranges</label>
                  <MultiSelect
                    options={ages.map(a => ({ value: a.id, label: a.range_name }))}
                    selectedValues={editedClinician.profile.ageRanges || []}
                    onChange={(selected) => handleInputChange('profile.ageRanges', selected)}
                    placeholder="Select age ranges"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
                  <MultiSelect
                    options={languages.map(l => ({ value: l.id, label: l.name }))}
                    selectedValues={editedClinician.profile.languages || []}
                    onChange={(selected) => handleInputChange('profile.languages', selected)}
                    placeholder="Select languages"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Accepted</label>
                  <MultiSelect
                    options={
                      insurances && insurances.length > 0
                        ? insurances.map((ins: any) => {
                            const insuranceName = ins.company_name || ins.name || String(ins);
                            return { 
                              value: insuranceName, 
                              label: insuranceName 
                            };
                          })
                        : []
                    }
                    selectedValues={editedClinician.profile.insuranceAccepted || []}
                    onChange={(selected) => handleInputChange('profile.insuranceAccepted', selected)}
                    placeholder="Select insurance accepted"
                  />
                  {(!insurances || insurances.length === 0) && (
                    <p className="text-xs text-gray-500 mt-2">Loading insurance options...</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'services':
        return (
          <div className="">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold mb-6">Session Types & Rates</h2>
              <div className="space-y-4">
                {editedClinician.profile.sessionTypes?.map((session, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                      <select
                        value={session.type}
                        onChange={(e) => {
                          const updated = [...editedClinician.profile.sessionTypes!];
                          updated[index] = { ...updated[index], type: e.target.value };
                          handleInputChange('profile.sessionTypes', updated);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Session Type</option>
                        {sessionTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={session.duration}
                        onChange={(e) => {
                          const updated = [...editedClinician.profile.sessionTypes!];
                          updated[index] = { ...updated[index], duration: parseInt(e.target.value) };
                          handleInputChange('profile.sessionTypes', updated);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Duration (minutes)"
                      />
                      <input
                        type="number"
                        value={session.rate || ''}
                        onChange={(e) => {
                          const updated = [...editedClinician.profile.sessionTypes!];
                          updated[index] = { ...updated[index], rate: parseFloat(e.target.value) };
                          handleInputChange('profile.sessionTypes', updated);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Rate ($)"
                      />
                      <input
                        type="text"
                        value={session.sessionTarget || ''}
                        onChange={(e) => {
                          const updated = [...editedClinician.profile.sessionTypes!];
                          updated[index] = { ...updated[index], sessionTarget: e.target.value };
                          handleInputChange('profile.sessionTypes', updated);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Session Target"
                      />
                    </div>
                    <button
                      onClick={() => removeFromArray('profile.sessionTypes', index)}
                      className="text-red-500 text-sm hover:text-red-700"
                    >
                      Remove Session Type
                    </button>
                  </div>
                ))}
                <div className="mt-4 space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                    <select
                      value={newSessionType.type}
                      onChange={(e) => setNewSessionType({ ...newSessionType, type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Session Type</option>
                      {sessionTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="Duration (minutes)"
                      value={newSessionType.duration}
                      onChange={(e) => setNewSessionType({ ...newSessionType, duration: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Rate ($)"
                      value={newSessionType.rate || ''}
                      onChange={(e) => setNewSessionType({ ...newSessionType, rate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="Session Target"
                      value={newSessionType.sessionTarget || ''}
                      onChange={(e) => setNewSessionType({ ...newSessionType, sessionTarget: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => {
                      if (newSessionType.type && newSessionType.duration) {
                        addToArray('profile.sessionTypes', {
                          type: newSessionType.type,
                          duration: parseInt(newSessionType.duration),
                          rate: newSessionType.rate ? parseFloat(newSessionType.rate) : undefined,
                          sessionTarget: newSessionType.sessionTarget || undefined
                        });
                        setNewSessionType({ type: '', duration: '', rate: '', sessionTarget: '' });
                      }
                    }}
                    className="text-blue-500 text-sm hover:text-blue-700 flex items-center"
                  >
                    <Plus className="mr-1" size={16} />
                    Add Session Type
                  </button>
                </div>
                </div>
            </div>

            {/* Max Sessions */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold mb-1">Max Sessions Per Week</h2>
              <p className="text-sm text-gray-500 mb-4">Maximum number of sessions this clinician can take per week.</p>
              <div className="max-w-xs">
                <input
                  type="number"
                  min={0}
                  value={editedClinician.profile.maxSessions?.perWeek ?? ''}
                  onChange={(e) =>
                    handleInputChange('profile.maxSessions', {
                      perWeek: e.target.value !== '' ? parseInt(e.target.value) : undefined,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. 30"
                />
              </div>
            </div>

          </div>
        );

      case 'availability':
        const daysOfWeek = [
          { key: 'monday', label: 'Monday' },
          { key: 'tuesday', label: 'Tuesday' },
          { key: 'wednesday', label: 'Wednesday' },
          { key: 'thursday', label: 'Thursday' },
          { key: 'friday', label: 'Friday' },
          { key: 'saturday', label: 'Saturday' },
          { key: 'sunday', label: 'Sunday' }
        ] as const;

        const addTimeSlot = (day: string) => {
          const currentSchedule = editedClinician.availability?.weeklySchedule?.[day as keyof typeof editedClinician.availability.weeklySchedule] || [];
          const newSlot = { start: '09:00', end: '17:00', available: true };
          const updatedSchedule = [...currentSchedule, newSlot];
          
          setEditedClinician(prev => ({
            ...prev,
            availability: {
              ...prev.availability,
              weeklySchedule: {
                ...prev.availability?.weeklySchedule,
                [day]: updatedSchedule
              }
            }
          }));
        };

        const removeTimeSlot = (day: string, index: number) => {
          const currentSchedule = editedClinician.availability?.weeklySchedule?.[day as keyof typeof editedClinician.availability.weeklySchedule] || [];
          const updatedSchedule = currentSchedule.filter((_, i) => i !== index);
          
          setEditedClinician(prev => ({
            ...prev,
            availability: {
              ...prev.availability,
              weeklySchedule: {
                ...prev.availability?.weeklySchedule,
                [day]: updatedSchedule
              }
            }
          }));
        };

        const updateTimeSlot = (day: string, index: number, field: 'start' | 'end', value: string) => {
          const currentSchedule = editedClinician.availability?.weeklySchedule?.[day as keyof typeof editedClinician.availability.weeklySchedule] || [];
          const updatedSchedule = currentSchedule.map((slot, i) => 
            i === index ? { ...slot, [field]: value } : slot
          );
          
          setEditedClinician(prev => ({
            ...prev,
            availability: {
              ...prev.availability,
              weeklySchedule: {
                ...prev.availability?.weeklySchedule,
                [day]: updatedSchedule
              }
            }
          }));
        };

        const av = editedClinician.availability;
        const openCount = av?.open?.length ?? 0;
        const bookedCount = av?.booked?.length ?? 0;
        const cancelledCount = av?.cancelled?.length ?? 0;
        const outOfOfficeCount = av?.outOfOffice?.length ?? 0;
        const blockedCount = av?.blocked?.length ?? 0;
        const hasCalendarData = openCount + bookedCount + cancelledCount + outOfOfficeCount + blockedCount > 0;

        return (
          <div className="">
            {hasCalendarData && (
              <div className="bg-white rounded-xl p-6 shadow-sm border mb-6">
                <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <CalendarDays className="text-indigo-600" size={22} />
                  Calendar availability (read-only)
                </h2>
                <p className="text-gray-600 text-sm mb-4">From last calendar scrape. Edit weekly schedule below to change recurring availability.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-4">
                  <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-emerald-800">Open</span>
                    <span className="text-lg font-bold text-emerald-700">{openCount}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-blue-800">Booked</span>
                    <span className="text-lg font-bold text-blue-700">{bookedCount}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-red-800">Cancelled</span>
                    <span className="text-lg font-bold text-red-700">{cancelledCount}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-orange-800">Out of office</span>
                    <span className="text-lg font-bold text-orange-700">{outOfOfficeCount}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-gray-800">Blocked</span>
                    <span className="text-lg font-bold text-gray-700">{blockedCount}</span>
                  </div>
                </div>
                {(clinician.availabilityUpdatedAt || clinician.lastCalendarScrape) && (
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500 border-t border-gray-100 pt-3">
                    {clinician.availabilityUpdatedAt && (
                      <span>Availability updated: {new Date(clinician.availabilityUpdatedAt).toLocaleString()}</span>
                    )}
                    {clinician.lastCalendarScrape && (
                      <span>Last scrape: {new Date(clinician.lastCalendarScrape).toLocaleString()}</span>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold mb-6">Weekly Availability</h2>
              <p className="text-gray-600 mb-6">Set your weekly schedule by adding time slots for each day.</p>
              
              <div className="space-y-6">
                {daysOfWeek.map((day) => {
                  const daySchedule = editedClinician.availability?.weeklySchedule?.[day.key] || [];
                  
                  return (
                    <div key={day.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-md font-medium text-gray-900">{day.label}</h3>
                        <button
                          onClick={() => addTimeSlot(day.key)}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Add Time Slot
                        </button>
                      </div>
                      
                      {daySchedule.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">No time slots added</p>
                      ) : (
                        <div className="space-y-3">
                          {daySchedule.map((slot, index) => (
                            <div key={index} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-center gap-2 flex-1">
                                <label className="text-sm text-gray-600">From:</label>
                                <input
                                  type="time"
                                  value={slot.start}
                                  onChange={(e) => updateTimeSlot(day.key, index, 'start', e.target.value)}
                                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <label className="text-sm text-gray-600 ml-2">To:</label>
                                <input
                                  type="time"
                                  value={slot.end}
                                  onChange={(e) => updateTimeSlot(day.key, index, 'end', e.target.value)}
                                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                              <button
                                onClick={() => removeTimeSlot(day.key, index)}
                                className="text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <main className="flex-1">
      <div className="min-h-screen bg-transparent relative">
        {/* Sticky Header */}
        <div className="bg-white/45 backdrop-blur-lg border-b border-white/30 sticky top-0 z-10">
          <div className="px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onCancel}
                  className="p-2 hover:bg-white/70 rounded-lg transition-colors shadow-sm border border-white/30 bg-white/60 backdrop-blur"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Edit Profile</h1>
                  <p className="text-sm text-gray-600">{editedClinician.fullName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-white/30 rounded-lg hover:bg-white/70 bg-white/50 backdrop-blur shadow-sm"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex">
          {/* Sidebar Navigation */}
          <div className="w-64 bg-white/45 backdrop-blur-lg border-r border-white/30 h-screen sticky top-0">
            <div className="p-6">
              <nav className="space-y-2">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors shadow-sm ${activeSection === item.id
                      ? 'bg-blue-100/40 text-blue-700 border border-white/30 backdrop-blur-md'
                      : 'text-gray-700 hover:bg-white/60 border border-transparent'
                      }`}
                  >
                    <span className="text-lg">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-8">
            <div className="mx-auto">
              {/* Public Visibility Alert */}
              <div className={`rounded-2xl shadow-lg p-6 mb-8 backdrop-blur-lg ${visibilityStatus.isComplete ? 'bg-green-50/50 border border-white/40' : 'bg-amber-50/50 border border-white/40'}`}>
                <div className="flex items-start">
                  {visibilityStatus.isComplete ? (
                    <CheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" size={24} />
                  ) : (
                    <AlertTriangle className="text-amber-600 mt-1 mr-3 flex-shrink-0" size={24} />
                  )}
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold mb-2 ${visibilityStatus.isComplete ? 'text-green-800' : 'text-amber-800'}`}>
                      {visibilityStatus.isComplete ? 'Profile Complete - Visible in Public Search' : 'Profile Incomplete - Not Visible in Public Search'}
                    </h3>
                    {visibilityStatus.isComplete ? (
                      <p className="text-green-700">
                        Great! This clinician profile is complete and will be visible to clients searching on the public platform.
                      </p>
                    ) : (
                      <div className="text-amber-700">
                        <p className="mb-3">
                          This clinician profile is missing required information and will not appear in public search results.
                          Complete the following fields to make this profile visible to potential clients:
                        </p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {visibilityStatus.missingFields.map((field, index) => (
                            <span key={index} className="bg-amber-200 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                              {field.name}
                            </span>
                          ))}
                        </div>
                        <div className="bg-amber-100/40 rounded-xl p-3 border border-white/40 backdrop-blur-md">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-amber-800">Completion Progress</span>
                            <span className="text-sm font-medium text-amber-800">
                              {visibilityStatus.completedCount}/{visibilityStatus.totalCount}
                            </span>
                          </div>
                          <div className="w-full bg-amber-200 rounded-full h-2">
                            <div
                              className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(visibilityStatus.completedCount / visibilityStatus.totalCount) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Public Card Preview */}
              {showPreview && (
                <div className="bg-white rounded-xl shadow-md p-8 mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Eye className="mr-2 text-blue-600" size={24} />
                    Public Search Card Preview
                  </h2>
                  <div className="max-w-md mx-auto">
                    <div className="bg-white rounded-lg shadow-md p-6 border">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-8 h-8 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{editedClinician.fullName}</h3>
                          <p className="text-sm text-gray-600">{editedClinician.titles?.join(', ')}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 mb-4 line-clamp-3">
                        {editedClinician.bio || 'No biography available'}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {editedClinician.specialties.slice(0, 3).map((specialty, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {specialty}
                          </span>
                        ))}
                      </div>
                      <div className="flex justify-between items-center text-sm text-gray-600">
                        <span>Status: {editedClinician.status}</span>
                        <span>{editedClinician.profile.languages?.length || 0} languages</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Section Content */}
              {renderSection()}

              {/* Save Button Section */}
              <div className="mt-8 pt-8 border-t border-white/30">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Save Changes</h3>
                    <p className="text-sm text-gray-600">
                      Click the save button below to update the clinician's information
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={onCancel}
                      className="px-6 py-3 text-gray-700 border border-white/40 rounded-xl hover:bg-white/60 transition-colors bg-white/40 backdrop-blur-md shadow-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-600/90 hover:to-indigo-600/90 transition-colors font-medium disabled:opacity-50 shadow-lg"
                      disabled={isSaving}
                      type="button"
                    >
                      {isSaving ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Save className="w-5 h-5" />
                      )}
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

// Reusable Components


export default ClinicianAdminEdit;
